import { type ThunkDispatch } from "@reduxjs/toolkit";
import { Auth, type User, getAuth } from "firebase/auth";
import { useFirebase } from "igfd-commons";

import React, { createContext, useContext, useEffect, useState } from "react";
import { connect } from "react-redux";

import { onUserIdChange } from "@dashboard/store/actions/adminUserActions";
import { type DashboardReducerAction } from "@dashboard/store/reducers/dashboardReducer";

import { onAuthChange } from "../monitoring";
import { type ReduxState, type ThunkExtraArgument } from "../types";

export type AuthState = {
  /**
   * Indicates if the auth state is still loading.
   *
   * Loading is set to false, when the auth state is known.
   *  - for a user that is logged in, the user is set
   *  - if a user wasn't logged in, user is set to null
   */
  loading: boolean;

  /**
   * If a user was logged in, this is the User, after loading this is the user.
   */
  user: User | null;

  auth: Auth;

  logout: () => Promise<void>;
};

const AuthStateCtx = createContext<AuthState>({} as AuthState);
const AuthStateProviderCore = ({
  onUserIdChange,
  children,
}: {
  onUserIdChange: (user: string | null) => void;
  children: React.ReactNode;
}) => {
  // this is the user's loading state, not the auth instance itself
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const firebase = useFirebase();

  const auth = getAuth(firebase.firebaseApp);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      // analytics and tracking
      onAuthChange(user);
      // call back to update other parts of the app
      onUserIdChange(user ? user.uid : null);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth]);

  return (
    <AuthStateCtx.Provider
      value={{
        loading,
        user,
        auth,
        logout: async () => {
          await auth.signOut();
        },
      }}
    >
      {children}
    </AuthStateCtx.Provider>
  );
};

export const AuthStateProvider = connect(
  null,
  (
    dispatch: ThunkDispatch<
      ReduxState,
      ThunkExtraArgument,
      DashboardReducerAction
    >
  ) => ({
    onUserIdChange: (userId: string | null) => dispatch(onUserIdChange(userId)),
  })
)(AuthStateProviderCore);

export const useAuthState = () => useContext(AuthStateCtx);
